import {MenuDeviceMetricNodesDto, MenuDeviceNodesDto} from "@widgets/widget-constructor/api/widget-constructor.service";
import {v4 as uuidv4} from "uuid";
import {UnitsEnum} from "@shared/enum";

// Конструктор виджетов дашборда: список станций и дерево устройств для
// выбора метрики. Названия и метрики построены по составу оборудования
// демонстрационного объекта №1 (stationMock/MQTT-METRICS.md §9, stationMock/stations/chulki.yaml).

type Unit = keyof typeof UnitsEnum;

function metric(localName: string, unit: Unit) {
    return {id: uuidv4(), local_name: localName, unit};
}

function deviceNode(name: string, nodes: any[] = []) {
    return {id: uuidv4(), name, nodes};
}

function metricNode(name: string, metrics: ReturnType<typeof metric>[], analytical: ReturnType<typeof metric>[] = [], nodes: any[] = []) {
    return {id: uuidv4(), name, metrics, analytical_metrics: analytical, nodes};
}

const currents3 = (): ReturnType<typeof metric>[] => [
    metric("Потребляемый ток фазы A", "MA"),
    metric("Потребляемый ток фазы B", "MA"),
    metric("Потребляемый ток фазы C", "MA"),
];
const stateMetric = () => metric("Состояние", "NONE");
const runningTime = () => metric("Наработка", "HOUR");
const analyticalPlaceholder = () => [metric("Прогноз наработки до отказа", "HOUR")];

// ---------- Станции ----------

export const deviceStationsEntities: MenuDeviceNodesDto = [
    deviceNode("Демонстрационный объект №1"),
    deviceNode("Демонстрационный объект №2"),
    deviceNode("Демонстрационный объект №3"),
];

export const metricStationsEntities: MenuDeviceMetricNodesDto = [
    metricNode("Демонстрационный объект №1", [
        metric("Расход сточных вод на входе", "CUBIC_METER_PER_HOUR"),
        metric("Расход сточных вод на выходе", "CUBIC_METER_PER_HOUR"),
        metric("Температура сточных вод на входе", "C"),
        metric("Общее энергопотребление станции", "KILOWATT_HOUR"),
    ], analyticalPlaceholder()),
    metricNode("Демонстрационный объект №2", [
        metric("Расход сточных вод на выходе", "CUBIC_METER_PER_HOUR"),
        metric("Температура окружающего воздуха", "C"),
    ], []),
    metricNode("Демонстрационный объект №3", [
        metric("Расход сточных вод на входе", "CUBIC_METER_PER_HOUR"),
    ], []),
];

// ---------- Дерево устройств (без метрик) ----------

export const devicesEntities: MenuDeviceNodesDto = [
    deviceNode("Предварительная мехочистка", [
        deviceNode("Вертикальная шнековая решётка"),
        deviceNode("Приёмная камера", [
            deviceNode("Насос дренажный №1"),
            deviceNode("Насос дренажный №2"),
        ]),
    ]),
    deviceNode("Реагентное хозяйство", [
        deviceNode("Гипохлорит натрия (ГХН) — дозатор №1"),
        deviceNode("Гипохлорит натрия (ГХН) — дозатор №2"),
        deviceNode("Щёлочь — дозатор №1"),
        deviceNode("Коагулянт — дозатор №1"),
    ]),
    deviceNode("Линия биологической очистки", [
        deviceNode("Воздуходувный модуль", [
            deviceNode("Воздуходувка №1 (20 кВт, УПП)"),
            deviceNode("Воздуходувка №2 (20 кВт, УПП)"),
            deviceNode("Воздуходувка №3 (20 кВт, УПП)"),
        ]),
        deviceNode("Группа насосов рециркуляции", [
            deviceNode("Группа насосов нитратного рецикла", [
                deviceNode("Насос рециркуляции №5"),
                deviceNode("Насос рециркуляции №6"),
            ]),
            deviceNode("Группа насосов камеры стабилизации", [
                deviceNode("Насос рециркуляции №1"),
                deviceNode("Насос рециркуляции №2"),
            ]),
            deviceNode("Группа насосов возвратного рецикла", [
                deviceNode("Насос рециркуляции №3"),
                deviceNode("Насос рециркуляции №4"),
            ]),
        ]),
        deviceNode("Насос подачи осадка на уплотнение"),
    ]),
    deviceNode("Модуль ультрафиолетового обеззараживания", [
        deviceNode("Насос чистой воды №1"),
        deviceNode("Фильтр сорбционный"),
        deviceNode("Фильтр механический"),
        deviceNode("Фильтр сорбционный"),
        deviceNode("Фильтр механический"),
        deviceNode("Насос чистой воды №2"),
        deviceNode("УФ-лампа"),
    ]),
    deviceNode("Узел обезвоживания осадка", [
        deviceNode("Установка приготовления флокулянта"),
        deviceNode("Обезвоживатель №1"),
        deviceNode("Обезвоживатель №2"),
    ]),
];

// ---------- Дерево устройств с метриками ----------

export const metricDevicesEntities: MenuDeviceMetricNodesDto = [
    metricNode("Линия биологической очистки", [
        metric("Температура в биореакторе", "C"),
        metric("Температура в денитрификаторе", "C"),
        metric("Водородный показатель pH", "NONE"),
        metric("Растворённый кислород", "MILLIGRAM_PER_LITER"),
    ], analyticalPlaceholder(), [
        metricNode("Насос подачи осадка на уплотнение", [
            ...currents3(),
            stateMetric(),
        ], analyticalPlaceholder()),
        metricNode("Группа насосов рециркуляции", [], analyticalPlaceholder(), [
            metricNode("Группа насосов возвратного рецикла", [], analyticalPlaceholder(), [
                metricNode("Насос рециркуляции №3", [stateMetric(), ...currents3(), runningTime()], analyticalPlaceholder()),
                metricNode("Насос рециркуляции №4", [stateMetric(), ...currents3(), runningTime()], analyticalPlaceholder()),
            ]),
            metricNode("Группа насосов нитратного рецикла", [], analyticalPlaceholder(), [
                metricNode("Насос рециркуляции №5", [stateMetric(), ...currents3(), runningTime()], analyticalPlaceholder()),
            ]),
            metricNode("Группа насосов камеры стабилизации", [], analyticalPlaceholder(), [
                metricNode("Насос рециркуляции №1", [...currents3(), stateMetric(), runningTime()], analyticalPlaceholder()),
            ]),
        ]),
        metricNode("Воздуходувный модуль", [
            metric("Давление в коллекторе", "MBAR"),
            metric("Температура в модуле", "C"),
        ], analyticalPlaceholder(), [
            metricNode("Воздуходувка №1 (20 кВт, УПП)", [
                metric("Потребляемый ток фазы B", "MA"),
                metric("Потребляемый ток фазы C", "MA"),
                metric("Вакууметрическое давление на входе", "MBAR"),
                metric("Избыточное давление на выходе", "MBAR"),
                stateMetric(),
                metric("Потребляемый ток фазы A", "MA"),
            ], analyticalPlaceholder()),
            metricNode("Воздуходувка №2 (20 кВт, УПП)", [
                metric("Потребляемый ток фазы B", "MA"),
                metric("Вакууметрическое давление на входе", "MBAR"),
                metric("Потребляемый ток фазы C", "MA"),
                metric("Потребляемый ток фазы A", "MA"),
                metric("Избыточное давление на выходе", "MBAR"),
                stateMetric(),
            ], analyticalPlaceholder()),
        ]),
    ]),
    metricNode("Модуль ультрафиолетового обеззараживания", [
        metric("Нижний уровень (дискретный)", "NONE"),
        metric("Аварийный уровень (дискретный)", "NONE"),
        metric("Верхний уровень (дискретный)", "NONE"),
        metric("Температура в модуле", "C"),
    ], analyticalPlaceholder(), [
        metricNode("Фильтр механический", [stateMetric(), metric("Давление после фильтра", "MBAR")], analyticalPlaceholder()),
        metricNode("Фильтр сорбционный", [stateMetric(), metric("Давление после фильтра", "MBAR")], analyticalPlaceholder()),
        metricNode("Насос чистой воды №1", [
            metric("Давление после насоса", "MBAR"),
            metric("Потребляемый ток фазы B", "MA"),
            stateMetric(),
            metric("Потребляемый ток фазы A", "MA"),
            metric("Потребляемый ток фазы C", "MA"),
        ], analyticalPlaceholder()),
        metricNode("Фильтр механический", [
            metric("Давление после фильтра", "MBAR"),
            stateMetric(),
        ], analyticalPlaceholder()),
        metricNode("Насос чистой воды №2", [
            metric("Потребляемый ток фазы A", "MA"),
            metric("Давление после насоса", "MBAR"),
            metric("Потребляемый ток фазы B", "MA"),
            metric("Потребляемый ток фазы C", "MA"),
            stateMetric(),
        ], analyticalPlaceholder()),
        metricNode("Фильтр сорбционный", [
            metric("Давление после фильтра", "MBAR"),
            stateMetric(),
        ], analyticalPlaceholder()),
        metricNode("УФ-лампа", [stateMetric(), runningTime()], analyticalPlaceholder()),
    ]),
    metricNode("Реагентное хозяйство", [], analyticalPlaceholder(), [
        metricNode("Гипохлорит натрия (ГХН) — дозатор №1", [stateMetric()], analyticalPlaceholder()),
        metricNode("Гипохлорит натрия (ГХН) — дозатор №2", [stateMetric()], analyticalPlaceholder()),
        metricNode("Щёлочь — дозатор №1", [stateMetric()], analyticalPlaceholder()),
        metricNode("Коагулянт — дозатор №1", [stateMetric()], analyticalPlaceholder()),
    ]),
    metricNode("Узел обезвоживания осадка", [], analyticalPlaceholder(), [
        metricNode("Обезвоживатель №1", [stateMetric()], analyticalPlaceholder()),
        metricNode("Обезвоживатель №2", [stateMetric()], analyticalPlaceholder()),
        metricNode("Установка приготовления флокулянта", [stateMetric(), metric("Низкий уровень флокулянта", "NONE")], analyticalPlaceholder()),
    ]),
];

export const datasetSettings = {
    min: -25,
    max: 35
};
